import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatGridListModule } from '@angular/material/grid-list';

import {
  DATE_FORMAT,
  AvailableSlots,
  Product,
  VALUE_CHANGES_DEBOUNCE,
  CalendarView,
  CALENDAR_VIEW_MODE,
} from '../../../models';
import {
  BusinessObjectUtils,
  ComparatorUtils,
  DateTimeUtils,
} from '../../../utils';
import { BaseComponent } from '../../../core';
import { LibCoreLayoutModule } from '../../core-layout';
import { PipesModule } from '../../../pipes';
import { CalendarSlotButtonComponent } from '../calendar-slot-button/calendar-slot-button.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CalendarCustomSlot } from '../model/CalendarCustomSlot.interface';
import { CalendarSlotDateRange } from '../model/CalendarSlotDateRange.interface';
import { CalendarDatePickerComponent } from '../calendar-date-picker/calendar-date-picker.component';

@Component({
  selector: 'lib-calendar-available-slot-selection-by-week',
  templateUrl: './calendar-available-slot-selection-by-week.component.html',
  styleUrls: ['./calendar-available-slot-selection-by-week.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    PipesModule,
    CalendarSlotButtonComponent,
    CalendarDatePickerComponent,
    // material design
    MatGridListModule,
    MatProgressBarModule,
  ],
})
export class CalendarAvailableSlotSelectionByWeekComponent extends BaseComponent {
  public readonly SCROLLING_SLOT_CONTAINER_ID = 'scrolling-time-slot-container';

  @Input() isLoading: boolean;

  private _minDate: Date;
  @Input() set minDate(value: Date) {
    this._minDate = value ? DateTimeUtils.getFirstDateOfWeek(value) : null;
  }
  get minDate(): Date {
    return this._minDate;
  }

  private _maxDate: Date;
  @Input() set maxDate(value: Date) {
    this._maxDate = value ? DateTimeUtils.getLastDateOfWeek(value) : null;
  }
  get maxDate(): Date {
    return this._maxDate;
  }

  private _availableSlots: AvailableSlots;
  @Input() set availableSlots(value: AvailableSlots) {
    this._availableSlots = value;

    if (value) {
      // order is important
      const currentCalendarView: CalendarView =
        this.controlSelectedDateRange?.value;

      const newWeekAsDate: Date = DateTimeUtils.getFirstDateOfWeek(
        DateTimeUtils.parseStringToDate(value.from)
      );

      if (
        !DateTimeUtils.isSameDate(newWeekAsDate, currentCalendarView?.fromDate)
      ) {
        this.controlSelectedDateRange.setValue({
          viewMode: currentCalendarView?.viewMode,
          fromDate: newWeekAsDate,
          toDate: DateTimeUtils.getLastDateOfWeek(newWeekAsDate),
        } as CalendarView);
      }

      this.updateAvailableSlotMatrix(value);
    }
  }
  get availableSlots(): AvailableSlots {
    return this._availableSlots;
  }

  private _disabled: boolean;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.controlSelectedDateRange.disable();
    } else {
      this.controlSelectedDateRange.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() selectedProducts: Product[];

  @Input() set presetSelectedSlot(value: Date) {
    this.selectSlotChanged(value);
  }

  @Output() selectedDateRage = new EventEmitter<CalendarSlotDateRange>(); // emit to trigger fetching available slots
  @Output() selectedSlot = new EventEmitter<Date>();

  public availableSlotMatrix: CalendarCustomSlot[][] = [];

  public controlSelectedDateRange: FormControl<CalendarView>;
  private currentSelectedSlot: Date;

  constructor() {
    super();

    this.controlSelectedDateRange = new FormControl<CalendarView>(
      {
        viewMode: CALENDAR_VIEW_MODE.WEEK,
        fromDate: null,
        toDate: null,
      },
      [Validators.required]
    );

    this.controlSelectedDateRange.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(ComparatorUtils.generateComparatorForObject()),
        debounceTime(VALUE_CHANGES_DEBOUNCE)
      )
      .subscribe((v) => {
        this.selectedDateRage.emit({
          from: v?.fromDate,
          to: v?.toDate,
        });
      });
  }

  public generateHtmlIdForSlot(slot: Date): string {
    return DateTimeUtils.formatDateTimeToString(
      slot,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
  }

  public isSlotSelected(d: Date): boolean {
    return (
      DateTimeUtils.isSameDate(d, this.currentSelectedSlot) &&
      DateTimeUtils.isSameTime(d, this.currentSelectedSlot, true)
    );
  }

  public isAnyDiscountInSlot(d: Date): boolean {
    return (
      this.selectedProducts?.findIndex((p) =>
        BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(p, d)
      ) >= 0
    );
  }

  public selectSlotChanged(d: Date): void {
    this.currentSelectedSlot = d;
    this.selectedSlot.emit(d);
    this.scrollToSelectedTimeSlot();
  }

  private updateAvailableSlotMatrix(appointmentTimeSlot: AvailableSlots): void {
    const mapOfSlotAndDateContainingSlot = new Map<string, string[]>();

    const setOfSlots = new Set<string>();
    appointmentTimeSlot?.slotsOfDates?.forEach((slotOfDate) => {
      slotOfDate?.times?.forEach((t) => {
        setOfSlots.add(t);

        if (mapOfSlotAndDateContainingSlot.has(t)) {
          mapOfSlotAndDateContainingSlot.get(t).push(slotOfDate.date);
        } else {
          mapOfSlotAndDateContainingSlot.set(t, [slotOfDate.date]);
        }
      });
    });
    const listOfSortedSlots = [...setOfSlots].sort();

    const currentSelectedDateRange = this.controlSelectedDateRange
      .value as CalendarView;
    const listOfDate: string[] = DateTimeUtils.getListOfDateString(
      DateTimeUtils.formatDateToString(
        currentSelectedDateRange.fromDate,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      ),
      DateTimeUtils.formatDateToString(
        currentSelectedDateRange.toDate,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      )
    );

    this.availableSlotMatrix = listOfSortedSlots?.map((slot) =>
      listOfDate?.map((date) => ({
        slot: DateTimeUtils.generateDateFromDateAndTimeString(date, slot),
        isAvailable: mapOfSlotAndDateContainingSlot.get(slot).includes(date),
      }))
    );

    this.scrollToSelectedTimeSlot();
  }

  private scrollToSelectedTimeSlot(): void {
    setTimeout(() => {
      if (this.currentSelectedSlot) {
        const slotElementId = this.generateHtmlIdForSlot(
          this.currentSelectedSlot
        );

        if (slotElementId) {
          const slotElement = document.getElementById(slotElementId);
          const containerElement = document.getElementById(
            this.SCROLLING_SLOT_CONTAINER_ID
          );

          if (slotElement && containerElement) {
            // if set containerElement.scrollTop = slotElement.offsetTop;
            // the slotElement sill be at the top and hide all previous slots.
            // let it down a little to show partly of previous slot
            const offsetTopPx = 30;

            containerElement.scrollTop = Math.max(
              slotElement.offsetTop - offsetTopPx,
              0
            );
          }
        }
      }
    }, 500);
  }
}
